import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, auth, type Brand, type ParamDef, type RunResult, type User } from '../api';
import { Banner, Spinner, useAsync } from '../components/Common';
import { ParamForm, defaultParamValues } from '../components/ParamForm';
import { BlockRenderer } from '../components/Blocks';
import { EmailForm } from '../components/EmailForm';
import { formatDateTime, relativeTime } from '../format';
import { Dashboard } from './Dashboard';

/**
 * Director view.
 *
 * For the people who read the numbers rather than build them (Mike, Thomas):
 * sign in, pick a report, pick a period, download it. Nothing else is on the
 * screen - no requests, stored data, schedules, entry points or run
 * diagnostics. Those exist and matter, but to a director they are noise, and
 * a screen full of plumbing reads as "this is not for me".
 *
 * It is a different SHELL over the same engine, not a second implementation.
 * Every figure still comes from the same stored run, so the PDF a director
 * downloads here is byte-for-byte what anyone else would get for the same
 * report and period. The caveats are kept too - folded into "About these
 * figures" rather than removed, because a number that is approximate has to
 * say so wherever it appears.
 *
 * Turned on per person in People (admins only). Someone with an editor or
 * admin role as well gets a "Full platform" link to switch back.
 */

interface ReportSummary {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  lastRun?: { id: string; started_at: string; status: string } | null;
}

interface ReportDetail {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  params: ParamDef[];
}

interface RecentRun {
  id: string;
  report_name: string;
  started_at: string;
  status: string;
}

/** The periods a director actually asks for, in the order they think of them. */
const PERIODS: { value: string; label: string }[] = [
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'Year to date' },
  { value: 'last_year', label: 'Last year' },
];
const OTHER_PERIOD_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This week',
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
};

/** Categories in the order a director scans them; anything new goes last. */
const CATEGORY_ORDER = ['Billings', 'Performance', 'Activity'];

function useHashRoute(): [string[], (path: string) => void] {
  const read = () => window.location.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const onChange = () => setRoute(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return [route, (path: string) => (window.location.hash = `#/${path.replace(/^\/+/, '')}`)];
}

export function DirectorShell({
  user,
  brand,
  onSignOut,
  onFullPlatform,
  previewing = false,
}: {
  user: User;
  brand: Brand | null;
  onSignOut: () => void;
  /** Present only for someone whose role also lets them build. */
  onFullPlatform?: () => void;
  /** An administrator looking at what directors see, not a director. */
  previewing?: boolean;
}) {
  const [route, navigate] = useHashRoute();
  const section = route[0] ?? 'reports';

  let body: React.ReactNode;
  if (section === 'reports' && route[1]) body = <DirectorReport key={route[1]} slug={route[1]} onBack={() => navigate('reports')} />;
  else if (section === 'runs' && route[1]) body = <DirectorReport key={route[1]} runId={route[1]} onBack={() => navigate('reports')} />;
  else if (section === 'week') body = <Dashboard user={user} simple onOpenReports={() => navigate('reports')} />;
  else body = <DirectorHome user={user} onOpen={(slug) => navigate(`reports/${slug}`)} onOpenRun={(id) => navigate(`runs/${id}`)} />;

  const nav = [
    { path: 'reports', label: 'Reports', active: section === 'reports' || section === 'runs' },
    { path: 'week', label: 'This week at a glance', active: section === 'week' },
  ];

  return (
    <div className="shell director">
      <aside className="sidebar">
        <div className="wordmark">
          <Logo name={brand?.name} />
        </div>
        <nav aria-label="Main">
          {nav.map((item) => (
            <a key={item.path} className={`nav-item${item.active ? ' active' : ''}`} href={`#/${item.path}`}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="spacer" />
        <div className="who">
          <b>{user.name}</b>
          <span>{user.email}</span>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              className="ghost small"
              style={{ color: 'rgba(255,255,255,.8)', paddingLeft: 0 }}
              onClick={async () => {
                await auth.logout();
                onSignOut();
              }}
            >
              Sign out
            </button>
            {onFullPlatform ? (
              <button className="ghost small" style={{ color: 'rgba(255,255,255,.8)' }} onClick={onFullPlatform}>
                Full platform
              </button>
            ) : null}
          </div>
        </div>
        {brand?.myScgUrl ? (
          <a className="back-link" href={brand.myScgUrl}>
            ← Back to My SCG
          </a>
        ) : null}
      </aside>
      <main className="main">
        {previewing ? (
          <div className="director-preview">
            <span>
              <strong>Preview.</strong> This is exactly what someone with director view sees — the same screens and the
              same reports.
            </span>
            {onFullPlatform ? (
              <button className="small" onClick={onFullPlatform}>
                Back to full platform
              </button>
            ) : null}
          </div>
        ) : null}
        {body}
      </main>
    </div>
  );
}

function Logo({ name }: { name?: string }) {
  const [failed, setFailed] = useState(false);
  const label = name ?? 'Spencer Clarke Group';
  if (failed) return <div className="fallback">{label}</div>;
  return <img src="/logo-white.png" alt={label} onError={() => setFailed(true)} />;
}

function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Europe/London' }).format(new Date()),
  );
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

// ------------------------------------------------------------------- home -----

function DirectorHome({
  user,
  onOpen,
  onOpenRun,
}: {
  user: User;
  onOpen: (slug: string) => void;
  onOpenRun: (id: string) => void;
}) {
  const reports = useAsync(() => api.get<ReportSummary[]>('/api/reports'));
  const recent = useAsync(() => api.get<RecentRun[]>('/api/runs?limit=6').catch(() => [] as RecentRun[]));
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (reports.data ?? []).filter(
      (r) => !q || r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q) || (r.category ?? '').toLowerCase().includes(q),
    );
    const cats = [...new Set(list.map((r) => r.category ?? 'Other reports'))].sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
    return cats.map((c) => ({ category: c, items: list.filter((r) => (r.category ?? 'Other reports') === c) }));
  }, [reports.data, query]);

  return (
    <>
      <div className="director-hero">
        <div className="greet">
          {greeting()}, {user.name.split(' ')[0]}
        </div>
        <h1>What would you like to pull?</h1>
        <p>Pick a report, choose the period, and download it as a PDF or Excel — or email it straight on.</p>
        {(reports.data?.length ?? 0) > 6 ? (
          <input
            className="director-search"
            type="search"
            placeholder="Find a report…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Find a report"
          />
        ) : null}
      </div>

      {reports.loading && !reports.data ? <Spinner label="Loading reports…" /> : null}
      {reports.error ? <Banner tone="error" title="Could not load your reports. ">{reports.error}</Banner> : null}
      {reports.data && !reports.data.length ? (
        <div className="empty-state">
          <h3>No reports yet</h3>
          <p>Ask IT to switch on the reports you need.</p>
        </div>
      ) : null}
      {reports.data && reports.data.length > 0 && !grouped.length ? (
        <div className="empty-state">
          <h3>Nothing matches “{query}”</h3>
        </div>
      ) : null}

      {grouped.map(({ category, items }) => (
        <section key={category} style={{ marginBottom: 26 }}>
          <h2 className="director-cat">{category}</h2>
          <div className="director-tiles">
            {items.map((r) => (
              <button key={r.slug} className="card director-tile" onClick={() => onOpen(r.slug)}>
                <span className="tile-name">{r.name}</span>
                {r.description ? <span className="tile-desc">{r.description}</span> : null}
                <span className="tile-go" aria-hidden>
                  Open →
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {(recent.data ?? []).length ? (
        <section style={{ marginTop: 8 }}>
          <h2 className="director-cat">Recently pulled</h2>
          <div className="card" style={{ padding: '4px 0' }}>
            {(recent.data ?? []).map((run) => (
              <button key={run.id} className="director-recent" onClick={() => onOpenRun(run.id)}>
                <span>{run.report_name}</span>
                <span className="small">{relativeTime(run.started_at)}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

// ----------------------------------------------------------------- report -----

function DirectorReport({ slug, runId, onBack }: { slug?: string; runId?: string; onBack: () => void }) {
  const [values, setValues] = useState<Record<string, any> | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<{ message: string; items: string[] } | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const lastRequest = useRef(0);

  const effectiveSlug = slug ?? result?.reportSlug;
  const report = useAsync(
    () => (effectiveSlug ? api.get<ReportDetail>(`/api/reports/${effectiveSlug}`) : Promise.resolve(null)),
    [effectiveSlug],
  );

  const periodParam = report.data?.params.find((p) => p.type === 'daterange');
  const otherParams = (report.data?.params ?? []).filter((p) => p !== periodParam);

  const run = async (next: Record<string, any>) => {
    if (!report.data) return;
    const ticket = ++lastRequest.current;
    setRunning(true);
    setError(null);
    setShowEmail(false);
    try {
      const res = await api.post<RunResult>(`/api/reports/${report.data.slug}/run`, { params: next, refresh: false });
      if (ticket === lastRequest.current) setResult(res);
    } catch (err) {
      if (ticket !== lastRequest.current) return;
      if (err instanceof ApiError) {
        setError({ message: err.message, items: Array.isArray(err.detail) ? (err.detail as string[]) : [] });
      } else setError({ message: String(err), items: [] });
    } finally {
      if (ticket === lastRequest.current) setRunning(false);
    }
  };

  // A stored run (from "Recently pulled", or an emailed link) shows exactly what
  // was generated then - not a fresh query that may disagree with the PDF
  // someone is already holding.
  useEffect(() => {
    if (!runId) return;
    setRunning(true);
    api
      .get<RunResult>(`/api/runs/${runId}`)
      .then(setResult)
      .catch((err) => setError({ message: err instanceof ApiError ? err.message : String(err), items: [] }))
      .finally(() => setRunning(false));
  }, [runId]);

  // Opening a report runs it straight away with its defaults: a director
  // clicked the tile because they want the numbers, not a form.
  useEffect(() => {
    if (!report.data || values !== null) return;
    const defaults = defaultParamValues(report.data.params);
    // A stored run opens with ITS period selected, so "Year to date" is lit
    // when that is what the figures on screen are.
    if (runId) {
      if (!result) return;
      setValues({ ...defaults, ...result.params });
      return;
    }
    setValues(defaults);
    run(defaults);
  }, [report.data, result]);

  const choosePeriod = (preset: string) => {
    if (!periodParam || !values) return;
    const next = { ...values, [periodParam.name]: { preset } };
    setValues(next);
    if (preset !== 'custom') run(next);
  };

  const currentPreset: string | undefined = periodParam && values ? values[periodParam.name]?.preset : undefined;
  const chips = useMemo(() => {
    const list = [...PERIODS];
    const def = (periodParam?.default as any)?.preset as string | undefined;
    for (const extra of [def, currentPreset]) {
      if (extra && extra !== 'custom' && !list.some((p) => p.value === extra) && OTHER_PERIOD_LABELS[extra]) {
        list.unshift({ value: extra, label: OTHER_PERIOD_LABELS[extra] });
      }
    }
    return list;
  }, [periodParam, currentPreset]);

  if (report.error && !result) {
    return (
      <>
        <BackLink onBack={onBack} />
        <Banner tone="error" title="This report could not be opened. ">
          {report.error}
        </Banner>
      </>
    );
  }

  const title = report.data?.name ?? result?.reportName ?? 'Report';
  // Failures first: when a report could not be fully produced, the reason is
  // the note that matters, and it must not be hidden behind the caveats.
  const notes = result ? [...result.warnings, ...result.accuracyNotes] : [];
  const periodLabel = result?.paramLabels.find((p) => p.name === periodParam?.name)?.display;

  return (
    <>
      <BackLink onBack={onBack} />
      <div className="page-head" style={{ marginTop: 6 }}>
        <div>
          <h1>{title}</h1>
          {report.data?.description ? <p>{report.data.description}</p> : null}
        </div>
      </div>

      {periodParam && values ? (
        <div className="director-periods" role="group" aria-label="Period">
          {chips.map((p) => (
            <button
              key={p.value}
              className={`chip${currentPreset === p.value ? ' on' : ''}`}
              aria-pressed={currentPreset === p.value}
              disabled={running && currentPreset === p.value}
              onClick={() => choosePeriod(p.value)}
            >
              {p.label}
            </button>
          ))}
          <button
            className={`chip${currentPreset === 'custom' ? ' on' : ''}`}
            aria-pressed={currentPreset === 'custom'}
            onClick={() => choosePeriod('custom')}
          >
            Choose dates…
          </button>
          {currentPreset === 'custom' ? (
            <CustomRange
              value={values[periodParam.name]}
              onApply={(from, to) => {
                const next = { ...values, [periodParam.name]: { preset: 'custom', from, to } };
                setValues(next);
                run(next);
              }}
            />
          ) : null}
        </div>
      ) : null}

      {otherParams.length && values ? (
        <div style={{ marginBottom: 14 }}>
          <button className="ghost small" onClick={() => setShowMore((v) => !v)} aria-expanded={showMore}>
            {showMore ? '▾ Fewer options' : '▸ More options'}
          </button>
          {showMore ? (
            <div className="filter-bar" style={{ marginTop: 8 }}>
              <ParamForm defs={otherParams} values={values} onChange={setValues} disabled={running} />
              <div className="filter-actions">
                <button className="primary" onClick={() => run(values)} disabled={running}>
                  Update
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="card director-problem">
          <h3>This report could not be produced just now</h3>
          <p>
            Usually that means one of the systems behind it did not answer. Try again in a few minutes — if it keeps
            happening, let IT know which report and period you picked.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary" onClick={() => values && run(values)} disabled={running}>
              Try again
            </button>
          </div>
          <details className="about-figures" style={{ marginTop: 12 }}>
            <summary>Technical detail for IT</summary>
            <p className="small">{error.message}</p>
            {error.items.length > 1 ? (
              <ul className="small">
                {error.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : null}
          </details>
        </div>
      ) : null}

      {running && !result ? <Spinner label="Pulling the figures…" /> : null}

      {result ? (
        <div className={running ? 'stale' : undefined}>
          <div className="director-actions card">
            <div className="as-at">
              <strong>{periodLabel ?? 'Report'}</strong>
              <span>
                Figures as at {formatDateTime(result.generatedAt)}
                {result.status === 'partial' ? ' · some figures missing, see below' : ''}
              </span>
            </div>
            <div className="buttons">
              <a className="btn primary-link" href={`/api/runs/${result.runId}/export.pdf`}>
                Download PDF
              </a>
              <a className="btn" href={`/api/runs/${result.runId}/export.xlsx`}>
                Download Excel
              </a>
              <button className="btn" onClick={() => setShowEmail((v) => !v)} aria-expanded={showEmail}>
                {showEmail ? 'Close' : 'Email'}
              </button>
              <a className="btn" href={`/api/runs/${result.runId}/print`} target="_blank" rel="noreferrer">
                Print
              </a>
            </div>
          </div>

          {showEmail ? (
            <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Email this report</h3>
              <EmailForm
                defaultAttachments={['pdf']}
                onSend={async ({ to, note, formats }) => {
                  const res = await api.post<{ detail: string }>(`/api/runs/${result.runId}/email`, { to, note, formats });
                  return res.detail;
                }}
                explain={<>The email carries the headline figures and every table, with the full report attached.</>}
              />
            </div>
          ) : null}

          {result.status === 'error' ? (
            <Banner tone="error" title="Some of this report could not be produced, so the figures below are incomplete. ">
              The reason is under “About these figures” at the bottom of the page — worth passing to IT.
            </Banner>
          ) : null}

          {result.blocks.map((block, i) => (
            <BlockRenderer key={block.id ?? i} block={block} data={result.data} />
          ))}

          {notes.length ? (
            <details className="about-figures card" open={result.status !== 'ok'}>
              <summary>
                About these figures <span className="small">({notes.length} note{notes.length === 1 ? '' : 's'})</span>
              </summary>
              <ul>
                {notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button className="ghost small director-back" onClick={onBack}>
      ← All reports
    </button>
  );
}

function CustomRange({ value, onApply }: { value: any; onApply: (from: string, to: string) => void }) {
  const [from, setFrom] = useState<string>(value?.from ?? '');
  const [to, setTo] = useState<string>(value?.to ?? '');
  const valid = Boolean(from && to && from <= to);
  return (
    <div className="custom-range">
      <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
      <span className="small">to</span>
      <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
      <button className="primary small" disabled={!valid} onClick={() => onApply(from, to)}>
        Show
      </button>
    </div>
  );
}
